import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Instagram, BadgeCheck, CircleDashed, ArrowRight } from "lucide-react";
import { getConversations, getConnections } from "@/lib/dentiumApi";

const CH_ICON = { whatsapp: MessageCircle, instagram: Instagram };
const CH_COLOR = { whatsapp: "#25D366", instagram: "#E1306C" };

const timeShort = (iso) => {
  try {
    const d = new Date(iso);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short" });
  } catch { return ""; }
};

const initials = (name = "") =>
  name.replace(/[@]/g, "").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "D";

export default function Home() {
  const navigate = useNavigate();
  const { data: connections = {} } = useQuery({ queryKey: ["connections"], queryFn: getConnections, refetchInterval: 10000 });
  const { data: conversations = [] } = useQuery({
    queryKey: ["conversations", "todos", "todos"],
    queryFn: () => getConversations("todos", "todos"),
    refetchInterval: 6000,
  });

  const recent = [...conversations]
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
    .slice(0, 8);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1100px] mx-auto space-y-6" data-testid="home-page">
      {/* Estado de conexiones */}
      <section>
        <h2 className="text-sm font-semibold text-[#8A7E70] uppercase tracking-wide mb-3">Estado de las conexiones</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {["whatsapp", "instagram"].map((id) => {
            const Icon = CH_ICON[id];
            const conn = connections[id];
            const isReal = conn?.mode === "real";
            return (
              <div key={id} data-testid={`home-connection-${id}`} className="rounded-2xl border border-[#E7E2DA] bg-white p-5 flex items-center gap-4">
                <span className="h-14 w-14 rounded-2xl flex items-center justify-center text-white shrink-0" style={{ background: CH_COLOR[id] }}>
                  <Icon className="h-7 w-7" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-base font-semibold text-[#2A2723] capitalize">{id}</div>
                  <div className="text-xs text-[#A79E90] truncate">{conn?.identifier || "Sin conectar"}</div>
                  <div className="mt-1.5">
                    {isReal ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600"><BadgeCheck className="h-4 w-4" /> Cuenta oficial conectada</span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#B9862E]"><CircleDashed className="h-4 w-4" /> Pendiente de conectar</span>
                    )}
                  </div>
                </div>
                <button
                  data-testid={`home-go-connect-${id}`}
                  onClick={() => navigate("/conectar")}
                  className="h-9 px-3 rounded-full text-xs font-medium border border-[#E0D3BC] text-[#6B6157] hover:bg-[#F1EBE1] transition-colors shrink-0"
                >
                  Gestionar
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Conversaciones recientes */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-[#8A7E70] uppercase tracking-wide">Conversaciones recientes</h2>
          <button
            data-testid="home-go-conversaciones"
            onClick={() => navigate("/conversaciones")}
            className="inline-flex items-center gap-1 text-xs font-medium text-[#1A1A1A] hover:underline"
          >
            Ver todas <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="rounded-2xl border border-[#E7E2DA] bg-white divide-y divide-[#EDE7DE] overflow-hidden">
          {recent.length === 0 && (
            <div className="p-10 text-center text-sm text-[#A79E90]">Aún no hay conversaciones.</div>
          )}
          {recent.map((c) => {
            const Icon = CH_ICON[c.channel] || MessageCircle;
            return (
              <button
                key={c.id}
                data-testid="home-recent-item"
                onClick={() => navigate("/conversaciones")}
                className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-[#FAF8F5] transition-colors"
              >
                <span className="h-11 w-11 rounded-full flex items-center justify-center text-white font-semibold text-sm shrink-0" style={{ background: CH_COLOR[c.channel] || "#1A1A1A" }}>
                  {initials(c.contact_name)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-[#1A1A1A] truncate">{c.contact_name || "Contacto anónimo"}</span>
                    <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: CH_COLOR[c.channel] }} />
                  </div>
                  <div className="text-xs text-[#8A7E70] truncate mt-0.5">{c.last_message || "Nueva conversación"}</div>
                </div>
                <span className="text-[11px] text-[#A79E90] shrink-0">{timeShort(c.updated_at)}</span>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
